import React, { useEffect, useState } from 'react';
import { message, Table, Upload, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../api';
import ExpertiseListModalComponent from '../../components/ExpertiseListModalComponent';
import { routerPath } from '../../router/routerPath';
import {
  BlueLayout,
  CustomButton,
  CustomTableContainer,
  DownloadCol,
  FormBlock,
  GridItem,
  ManualContainer,
  PagesContainerUI,
} from '../../styles/styled';
import { getBase64 } from '../../utils/convertToBase64';
import { StatusType } from '../ApplicationPage/types';
import { ExpertisesType, ExpertiseTextbookType, ExpertiseType } from '../ExpertisePage/types';
import { ExpertiseStatusSvgGenerator } from '../ExpertProfilePage/components/ExpertProfileComponent/ExpertProfileComponent';
import { FileDataType } from '../NewApplicationPage/types';
import { FileResponseType } from '../NewDocumentCategory/components/AddFIleForm/types';
import { ExpertiseListType } from './types';

export type LocaleExpertiseType = ExpertiseType & {
  key: number;
};

export type LocaleExpertisesType = LocaleExpertiseType[];

export const ExpertExpertisePage = () => {
  const [expertisesState, setExpertises] = useState<LocaleExpertisesType>([]);
  const [openModal, setOpen] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<number | undefined>();
  const [fileLoadState, setFileLoad] = useState<FileDataType | null>(null);
  const [currentExpertiseListId, setCurrentExpertiseListId] = useState<number | undefined>();
  const navigate = useNavigate();
  const [fileLoad, setLoad] = useState(false);
  const [currentPovCode, setCurrentPovCode] = useState<string | undefined>();
  const [chosenExpertiseList, setChosenExpertiseList] = useState<ExpertiseType | undefined>();
  const [loadData, setLoadData] = useState<boolean>(true);

  const handleEl: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'error') setLoad(true);

    getBase64(info.file.originFileObj as RcFile, (url) =>
      setFileLoad({
        base64: url.split('base64,')[1],
        title: info.file.name as string,
        ext: info.file.name.split('.')[1],
      }),
    );

    return;
  };

  useEffect(() => {
    if (fileLoad && currentExpertiseListId)
      api.post<FileResponseType>(endpoints.addFile(), fileLoadState).then((el) =>
        api
          .put(endpoints.loadFinalList(currentExpertiseListId), {
            final_exp_file: { id: el.data.file_id },
          })
          .then(() => {
            message.success('Итоговый ЭЛ успешно загружен');
            setLoadData(true);
            setLoad(false);
          })
          .catch(() => message.error('Произошла ошибка при загрузке файла')),
      );

    // eslint-disable-next-line
  }, [fileLoadState]);

  useEffect(() => {
    if (loadData)
      (async () => {
        try {
          const response = await api.get<ExpertisesType>(endpoints.getCurrentExpertExpertises());

          setExpertises(
            response.data.map((el, index) => {
              return { ...el, key: index + 1 };
            }),
          );
        } catch (err) {}
      })();

    setLoadData(false);
  }, [loadData]);

  const expertExpertiseColumns = [
    {
      title: '№ п/п',
      dataIndex: 'key',
      key: 'number',
    },
    {
      title: 'Наименование учебника',
      dataIndex: 'textbook',
      key: 'title',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.title}</p>,
    },
    {
      title: 'Класс',
      dataIndex: 'textbook',
      key: 'school_classes',
      render: (textbook: ExpertiseTextbookType) => (
        <p>{textbook?.school_classes?.length > 0 && textbook?.school_classes[0]?.title}</p>
      ),
    },
    {
      title: 'Автор (авторский коллектив)',
      dataIndex: 'textbook',
      key: 'author',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.author}</p>,
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_title',
      render: (textbook: ExpertiseTextbookType) => {
        return textbook?.text_book_edu_man?.map(
          (el, idx) => el.title && <ManualContainer key={`${textbook.key}-${idx}`}>{el.title}</ManualContainer>,
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_author',
      render: (textbook: ExpertiseTextbookType) => {
        return textbook?.text_book_edu_man?.map(
          (el, idx) => el.fio && <ManualContainer key={`${textbook.key}-${idx}`}>{el.fio}</ManualContainer>,
        );
      },
    },
    {
      title: 'Наименование издательства',
      dataIndex: 'textbook',
      key: 'publisher',
      render: (textbook: ExpertiseTextbookType) => <p>{textbook?.publisher?.title}</p>,
    },
    {
      title: 'Дата загрузки итогового ЭЛ',
      dataIndex: 'textbook',
      key: 'number',
      render: (textbooks: ExpertiseTextbookType, expertise: ExpertiseType) => {
        return (
          <p>{expertise?.expertise_list?.date_loading_final_list?.split('T')[0].split('-').reverse().join('.')}</p>
        );
      },
    },
    {
      title: 'Статус экспертизы',
      dataIndex: 'status',
      key: 'status',
      render: (status: StatusType) => ExpertiseStatusSvgGenerator(status),
    },
    {
      title: 'Статус ЭЛ',
      dataIndex: 'expertise_list',
      key: 'status',
      render: (expertiseList: ExpertiseListType) => <p>{expertiseList?.status?.name}</p>,
    },
    {
      title: 'Экспертиза',
      dataIndex: 'id',
      key: 'expertise',
      render: (id: number, expertise: ExpertiseType) => (
        <GridItem gap="5px" justify="flex-start" align="center">
          <CustomButton
            onClick={() => {
              setChosenExpertiseList(expertise);

              if (expertise?.expertise_list?.status?.code === 'complete') {
                setCurrentId(expertise?.expertise_list?.id);
                setOpen(true);
                setCurrentPovCode(undefined);
              } else if (expertise?.expertise_list?.status?.code === 'refactoring') {
                setCurrentId(expertise?.expertise_list?.id);
                setCurrentPovCode('refactoring');
                setOpen(true);
              } else if (expertise?.expertise_list?.status?.code === 'sent') {
                setCurrentId(expertise?.expertise_list?.id);
                setCurrentPovCode(undefined);
                setOpen(true);
              } else {
                navigate(routerPath.expertExpertise + id);
                setCurrentPovCode(undefined);
              }
            }}
            lined
            height="25px"
            padding="0 8px"
            width="120px"
            fontsize="10px !important"
            nowrap
          >
            Экспертный лист
          </CustomButton>
          {expertise?.expertise_list?.final_exp_file && (
            <a href={expertise.expertise_list.final_exp_file?.file_path} download target="_blank" rel="noreferrer">
              <CustomButton height="25px" padding="0 8px" width="120px" fontsize="10px !important" nowrap>
                Итоговый эл
              </CustomButton>
            </a>
          )}
          {expertise?.expertise_list?.status?.code === 'complete' && (
            <button className="empty-button" onClick={() => setCurrentExpertiseListId(expertise?.expertise_list?.id)}>
              <DownloadCol noBorder span={24}>
                <Upload onChange={handleEl} accept=".pdf, .png, .jpg, .jpeg" name="avatar" showUploadList={false}>
                  <div className="download-el">
                    {!expertise?.expertise_list?.final_exp_file ? 'Загрузить итоговый ЭЛ' : 'Заменить'}
                  </div>
                </Upload>
              </DownloadCol>
            </button>
          )}
        </GridItem>
      ),
      // (
      //   <CustomButton
      //     onClick={() => navigate(routerPath.expertExpertise + id)}
      //     lined
      //     height="25px"
      //     padding="0 8px"
      //     width="120px"
      //     fontsize="10px !important"
      //     nowrap
      //   >
      //     Экспертный лист
      //   </CustomButton>
      // ),
    },
  ];

  return (
    <PagesContainerUI>
      <br />
      <ExpertiseListModalComponent
        expertise_list={chosenExpertiseList}
        isPovs
        setLoad={() => setLoadData(true)}
        isExpert
        id={currentId}
        isRejected={!!currentPovCode}
        open={openModal}
        setOpen={(value) => setOpen(value)}
      />
      <BlueLayout>Экспертизы</BlueLayout>

      <FormBlock white noBorderRadius>
        <CustomTableContainer>
          <Table columns={expertExpertiseColumns} dataSource={expertisesState} />
        </CustomTableContainer>
      </FormBlock>
    </PagesContainerUI>
  );
};
